import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import { RootState } from "@/components/state/store";
import LmsEnrolmentsTable from "@/components/lms/lmsEnrolmentsTable";
import LmsEnrolmentCreateModal from "@/components/lms/lmsEnrolmentCreateModal";
import { buildEnrolmentPresetsFromSubjectSearch } from "@/components/lms/lmsOverviewMetricsHelpers";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import {
  getEffectiveLmsMode,
  getLmsCoursesRoute,
  LmsMode,
  shouldBlockLmsForSubdomain,
} from "@/components/lms/lms.utils";

interface Props {
  requiredMode?: LmsMode;
}

export default function LmsEnrolments({ requiredMode }: Props) {
  const { t } = useTranslation();
  usePageHeading(t("course_progress"));
  const loginState = useSelector((state: RootState) => state.login);
  const subjectSearch = useSelector((state: RootState) => state.subjectSearch);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const {
    presetParticipants: enrolmentPresetParticipants,
    presetCompanies: enrolmentPresetCompanies,
  } = useMemo(
    () => buildEnrolmentPresetsFromSubjectSearch(subjectSearch),
    [subjectSearch],
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const { handleError, errorModal } = useErrorModal();
  const [notificationMessage, setNotificationMessage] = useState("");

  const subdomain = window.location.hostname.split(".")[0];
  const shouldBlockLms = shouldBlockLmsForSubdomain({
    subdomain,
    permissions: loginState.permissions,
  });
  const effectiveMode = getEffectiveLmsMode({
    subdomain,
    permissions: loginState.permissions,
  });

  if (shouldBlockLms) {
    return <Navigate to="/" replace />;
  }

  if (requiredMode && effectiveMode !== requiredMode) {
    return <Navigate to={getLmsCoursesRoute(effectiveMode)} replace />;
  }

  const handleRefresh = () => {
    setRefreshKey((currentValue) => currentValue + 1);
  };

  const handleCreateCompleted = () => {
    setIsCreateModalOpen(false);
    handleRefresh();
    setNotificationMessage(t("users_enrolled_successfully"));
  };

  return (
    <>
      <LmsEnrolmentsTable
        mode={effectiveMode}
        includeCourseColumn
        onCreate={() => setIsCreateModalOpen(true)}
        refreshKey={refreshKey}
        onRefreshed={handleRefresh}
        onError={handleError}
      />

      <LmsEnrolmentCreateModal
        open={isCreateModalOpen}
        mode={effectiveMode}
        onClose={() => setIsCreateModalOpen(false)}
        onCompleted={handleCreateCompleted}
        enableCourseSelection={true}
        presetParticipants={enrolmentPresetParticipants}
        presetCompanies={enrolmentPresetCompanies}
      />

      {notificationMessage && (
        <GenericNotification
          message={notificationMessage}
          variant="success"
          handleCloseSnackbar={() => setNotificationMessage("")}
        />
      )}

      {errorModal}
    </>
  );
}
